import { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import ExternalAttachment from 'components/shared/ExternalAttachment';
import FileDropZone from 'components/shared/FileDropZone';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import StandardModal, { Actions, ColorThemeName, ModalContent } from 'components/shared/StandardModal';
import Upload from 'hooks/useDirectUpload/Upload';
import useDirectUpload from 'hooks/useDirectUpload/useDirectUpload';
import useInfiniteOptions from 'hooks/useInfiniteOptions';
import { adminClientsQueryKeys, adminIndexClients } from 'services/api/admin/clients';
import {
  createHighCostMedicationListImport,
  HighCostMedicationListImport,
} from 'services/api/admin/highCostMedicationListImports';
import { colors } from 'styles/theme/colors';
import ClipIcon from 'svg/AttachFileIcon';
import TrashIcon from 'svg/TrashIcon';

// The importer compares the CSV's header row to this list exactly, so all six must be present in
// this order (HighCostMedicationListImport::Csv::REQUIRED_HEADERS). `optional` marks the ones whose
// cells may be blank — only Generic Name needs a value in every row. See design 002, "Upload modal".
const COLUMNS = [
  { header: 'Generic Name', optional: false },
  { header: 'Brand Name', optional: true },
  { header: 'Cost Tier', optional: true },
  { header: 'Dosage', optional: true },
  { header: 'Route', optional: true },
  { header: 'Form', optional: true },
];

// Mirror the backend's authoritative cap (HighCostMedicationListImport::Importer::MAX_FILE_SIZE).
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
// CSV only. The '.csv' extension entry still accepts files the OS mislabels (e.g. Windows reports a
// .csv's MIME as application/vnd.ms-excel) via extension match, but a real .xls/.xlsx is rejected.
const CSV_TYPES = { 'text/csv': ['.csv'] };

type ClientOption = { id: string; name: string };

// TrashIcon defaults to 16px; the attached-file row wants 12. Declared at module scope so its
// identity is stable across renders rather than remounting the icon on every keystroke.
function SmallTrashIcon(props: React.SVGProps<SVGSVGElement>) {
  return <TrashIcon {...props} width={12} height={12} />;
}

type Props = {
  onClose: () => void;
  onUploaded: (highCostMedicationListImport: HighCostMedicationListImport) => void;
};

export default function UploadHighCostMedListModal({ onClose, onUploaded }: Props) {
  const [client, setClient] = useState<ClientOption | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Synchronous double-submit guard. `submitting` state (and the button's disabled/loading props)
  // update asynchronously, so two rapid clicks both read the stale value and fire two POSTs —
  // creating a duplicate import that then loses the one-active-list-per-client race and strands the
  // UI on "processing". A ref flips synchronously, so the second click is a no-op.
  const submittingRef = useRef(false);

  // Only clients with inbound referrals enabled AND no active list (the
  // `high_cost_med_list_eligible` filter). The list row is created by a successful import, so a
  // client stays eligible while an import is in flight and a second upload for it still loses the
  // one-active-list-per-client check in the importer. Shares the query cache with the tracker's
  // reset via the same key.
  const clientOptions = useInfiniteOptions<ClientOption>({
    queryKey: adminClientsQueryKeys.highCostMedListEligible,
    fetchFn: (params, signal) => adminIndexClients(params, signal),
    params: { sortBy: 'name asc', highCostMedListEligible: true },
  });

  // Direct-to-S3 upload; the completed Upload carries the ActiveStorage signed id we send on.
  const { uploads, uploadFiles, removeUpload } = useDirectUpload([]);
  const upload: Upload | undefined = uploads[0];
  const fileReady = !!upload?.isComplete && !!upload.signedId;

  const canUpload = !!client && fileReady && !submitting;

  const handleFiles = (files: File[]) => {
    setError(null);
    const file = files[0];
    if (!file) return;

    // Reject oversize files up front so we never upload them to S3 or enable the Upload button
    // (the backend enforces the same cap authoritatively).
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setError('The file is larger than 10 MB.');
      return;
    }

    uploadFiles([file]);
  };

  const handleUpload = async () => {
    if (submittingRef.current || !canUpload || !upload) return;
    submittingRef.current = true;

    setSubmitting(true);
    setError(null);
    try {
      const created = await createHighCostMedicationListImport({
        clientId: client!.id,
        signedId: upload.signedId!,
        filename: upload.filename,
      });
      onUploaded(created);
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'The high cost med list could not be uploaded. Please try again.');
      setSubmitting(false);
      // Re-open the gate so the user can retry after a failure (success unmounts the modal).
      submittingRef.current = false;
    }
  };

  const dropzoneIcon = useMemo(() => <ClipIcon height={16} />, []);

  return (
    <StandardModal title='Upload High Cost Med List' colorTheme={ColorThemeName.Primary} onCancel={onClose}>
      <ModalContent>
        <Intro>
          Attach a .csv file to upload a client&apos;s high cost medication list. The file requires the following column
          headers:
          <HeaderList>
            {COLUMNS.map(({ header, optional }) => (
              <li key={header}>{optional ? `${header} (Optional)` : header}</li>
            ))}
          </HeaderList>
        </Intro>

        <Field>
          <FieldLabel>Client</FieldLabel>
          <Select<ClientOption>
            {...clientOptions}
            value={client}
            onChange={setClient}
            getOptionValue={(option) => option.id}
            getOptionLabel={(option) => option.name}
            placeholder='Client'
          />
        </Field>

        <FileDropZone
          onFilesSelected={handleFiles}
          allowedTypes={CSV_TYPES}
          maxFiles={1}
          currentFileCount={upload ? 1 : 0}
          noClick={!!upload}
          noDrag={!!upload}>
          {(dropzone) => (
            <DropArea {...dropzone.getRootProps()} $active={dropzone.isDragActive} $disabled={!!upload}>
              <input {...dropzone.getInputProps()} />
              {dropzoneIcon}
              <DropLabel>
                Drag and drop your file(s) here, or&nbsp;
                <BrowseLink aria-label='launch file browser'>browse your files</BrowseLink>
              </DropLabel>
            </DropArea>
          )}
        </FileDropZone>

        {upload && (
          <AttachedFileRow>
            {/* The shared attachment row, so this matches every other upload surface — and it shows
                a progress bar while the file is still going up to S3. The document-type menu is
                suppressed: a high cost med CSV has one shape, the importer never reads a doc type,
                and the create endpoint sends only the signed id and filename. */}
            <AttachedFile
              attachment={upload}
              error={upload.hasError}
              uploadProgress={upload.progress}
              onDeleteAttachment={removeUpload}
              canModifyDocumentType={false}
              DeleteIcon={SmallTrashIcon}
            />
          </AttachedFileRow>
        )}

        {error && <ErrorText>{error}</ErrorText>}
      </ModalContent>

      <Actions>
        <Button variant='ghost' onClick={onClose}>
          Cancel
        </Button>
        <Button variant='primary' disabled={!canUpload} loading={submitting} onClick={handleUpload}>
          Upload
        </Button>
      </Actions>
    </StandardModal>
  );
}

const Intro = styled.div`
  font-size: 14px;
  color: ${colors.black100};
  line-height: 1.5;
  text-align: left;
`;

const HeaderList = styled.ul`
  margin: 4px 0 0;
  padding-left: 24px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 20px;
  text-align: left;
`;

const FieldLabel = styled.label`
  font-size: 12px;
  color: ${colors.black75};
`;

const DropArea = styled.div<{ $active: boolean; $disabled: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 20px;
  padding: 32px 16px;
  border: 1px dashed ${({ $active }) => ($active ? colors.blue100 : colors.black25)};
  border-radius: 8px;
  background: ${({ $active }) => ($active ? colors.blue5 : 'transparent')};
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  text-align: center;
`;

const DropLabel = styled.span`
  font-size: 14px;
  color: ${colors.black75};
`;

const BrowseLink = styled.span`
  color: ${colors.blue100};
  text-decoration: underline;
`;

// ExternalAttachment carries its own fill, border and radius; this only spaces it below the drop
// zone and keeps the text left-aligned inside the centered modal body.
const AttachedFileRow = styled.div`
  margin-top: 16px;
  text-align: left;
`;

// The design calls for a 40px row here; the shared component's 32px default stays put for every
// other attachment surface.
const AttachedFile = styled(ExternalAttachment)`
  min-height: 40px;
`;

const ErrorText = styled.p`
  margin: 12px 0 0;
  font-size: 13px;
  color: ${colors.red100};
`;
